import React from 'react';
import * as Sentry from '@sentry/react';

const SentryErrorFallback = ({ error, resetError }) => {
  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        <h2 className="text-xl font-semibold text-neutral-800 mb-2">
          Oops! Something went wrong
        </h2>

        <p className="text-stone-600 mb-6">
          We're sorry, but something unexpected happened. Our team has been
          notified and is working on a fix.
        </p>

        {process.env.NODE_ENV === 'development' && error && (
          <details className="mb-4 p-3 bg-neutral-50 rounded text-left text-sm">
            <summary className="cursor-pointer font-medium text-stone-700 mb-2">
              Error Details (Development)
            </summary>
            <pre className="text-red-600 whitespace-pre-wrap overflow-auto max-h-32">
              {error.toString()}
              {error.stack}
            </pre>
          </details>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={resetError}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={() => (window.location.href = '/')}
            className="px-4 py-2 bg-stone-200 text-neutral-700 rounded-lg hover:bg-stone-300 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

// Create Sentry-enhanced error boundary
const SentryErrorBoundary = ({ children, fallback, showDialog = false }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={fallback || SentryErrorFallback}
      showDialog={showDialog}
      dialogOptions={{
        title: "It looks like we're having issues.",
        subtitle: 'Our team has been notified.',
        subtitle2: "If you'd like to help, tell us what happened below.",
        labelName: 'Name',
        labelEmail: 'Email',
        labelComments: 'What happened?',
        labelClose: 'Close',
        labelSubmit: 'Submit',
        errorGeneric:
          'An unknown error occurred while submitting your report. Please try again.',
        errorFormEntry:
          'Some fields were invalid. Please correct the errors and try again.',
        successMessage: 'Your feedback has been sent. Thank you!',
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default SentryErrorBoundary;
